import { Controller } from '@hotwired/stimulus'

const ACTIVE_CLASSES = [
  'bg-zinc-900',
  'text-zinc-100',
  'dark:bg-zinc-100',
  'dark:text-zinc-900',
  'shadow-sm',
  'ring-1',
  'ring-zinc-900/15',
  'dark:ring-zinc-100/20',
  'font-semibold'
]

const INACTIVE_CLASSES = [
  'text-zinc-600',
  'dark:text-zinc-300',
  'hover:bg-zinc-100',
  'dark:hover:bg-zinc-800'
]

export default class extends Controller {
  static targets = ['button']

  static values = {
    lightThemeColor: String,
    darkThemeColor: String
  }

  connect() {
    this.boundHandleSystemThemeChange = this.handleSystemThemeChange.bind(this)
    this.darkModeMedia = window.matchMedia('(prefers-color-scheme: dark)')
    this.darkModeMedia.addEventListener?.('change', this.boundHandleSystemThemeChange)
  }

  disconnect() {
    this.darkModeMedia?.removeEventListener?.('change', this.boundHandleSystemThemeChange)
  }

  select(event) {
    event.preventDefault()

    const button = event.currentTarget
    const preference = button.dataset.themeToggleThemeValue
    if (!preference) return

    this.applyTheme(preference)
    this.persistPreference(preference, button.form?.action)
  }

  handleSystemThemeChange() {
    if (document.documentElement.dataset.themePreference !== 'system') return

    this.applyTheme('system')
  }

  applyTheme(preference) {
    const effectiveTheme = this.resolveEffectiveTheme(preference)

    document.documentElement.dataset.themePreference = preference
    document.documentElement.dataset.effectiveTheme = effectiveTheme

    this.updateButtons(preference)
    this.updateThemeMetadata(effectiveTheme, preference)
  }

  resolveEffectiveTheme(preference) {
    if (preference === 'dark' || preference === 'light') {
      return preference
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  updateButtons(activePreference) {
    this.buttonTargets.forEach((button) => {
      const isActive = button.dataset.themeToggleThemeValue === activePreference

      button.setAttribute('aria-pressed', String(isActive))

      if (isActive) {
        button.classList.remove(...INACTIVE_CLASSES)
        button.classList.add(...ACTIVE_CLASSES)
      } else {
        button.classList.remove(...ACTIVE_CLASSES)
        button.classList.add(...INACTIVE_CLASSES)
      }
    })
  }

  updateThemeMetadata(theme, preference) {
    if (preference === 'system') {
      this.removeThemeOverrideMeta()
      this.ensureSystemThemeMetaTags()
    } else {
      this.removeSystemThemeMetaTags()
      const themeColorMeta = this.ensureThemeOverrideMeta()
      themeColorMeta.setAttribute('content', theme === 'dark' ? this.darkThemeColorValue : this.lightThemeColorValue)
    }

    const statusBarMeta = document.querySelector("meta[name='apple-mobile-web-app-status-bar-style'][data-dynamic-status-bar='true']")
    if (statusBarMeta) {
      statusBarMeta.setAttribute('content', theme === 'dark' ? 'black-translucent' : 'default')
    }
  }

  ensureThemeOverrideMeta() {
    let themeColorMeta = document.querySelector("meta[name='theme-color'][data-theme-override='true']")
    if (themeColorMeta) return themeColorMeta

    themeColorMeta = document.createElement('meta')
    themeColorMeta.setAttribute('name', 'theme-color')
    themeColorMeta.setAttribute('data-theme-override', 'true')
    document.head.appendChild(themeColorMeta)

    return themeColorMeta
  }

  removeThemeOverrideMeta() {
    document.querySelector("meta[name='theme-color'][data-theme-override='true']")?.remove()
  }

  ensureSystemThemeMetaTags() {
    this.ensureSystemThemeMetaTag('light', this.lightThemeColorValue)
    this.ensureSystemThemeMetaTag('dark', this.darkThemeColorValue)
  }

  ensureSystemThemeMetaTag(theme, color) {
    let meta = document.querySelector(`meta[name='theme-color'][media='(prefers-color-scheme: ${theme})']`)
    if (meta) {
      meta.setAttribute('content', color)
      return
    }

    meta = document.createElement('meta')
    meta.setAttribute('name', 'theme-color')
    meta.setAttribute('media', `(prefers-color-scheme: ${theme})`)
    meta.setAttribute('content', color)
    document.head.appendChild(meta)
  }

  removeSystemThemeMetaTags() {
    document.querySelector("meta[name='theme-color'][media='(prefers-color-scheme: light)']")?.remove()
    document.querySelector("meta[name='theme-color'][media='(prefers-color-scheme: dark)']")?.remove()
  }

  persistPreference(preference, actionUrl) {
    if (!actionUrl) return

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    if (!csrfToken) return

    fetch(actionUrl, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ theme_preference: preference })
    })
  }
}
